import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useAccessToken } from '@/services/auth';
import { cellLinesOptions } from '@/services/lookups';
import { CellLineResponse } from '@/services/lookups/types';

type Props = {
  value?: string | null;
  onChange?: (item: CellLineResponse | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

export const CellLineAutoComplete = ({
  value = null,
  onChange,
  placeholder = 'Select cell line',
  className,
  disabled,
}: Props) => {
  const token = useAccessToken();
  const { data: items = [], isLoading } = useQuery(cellLinesOptions(token));
  const [selected, setSelected] = useState<CellLineResponse | null>(null);

  useEffect(() => {
    if (!items.length || !value) {
      if (!value) setSelected(null);
      return;
    }
    const found = items.find((item) => item.id === value);
    if (found && found.id !== selected?.id) {
      setSelected(found);
    }
  }, [items, value]);

  const commit = useMemo(
    () => (item: CellLineResponse | null) => {
      setSelected(item);
      onChange?.(item);
    },
    [onChange],
  );

  return (
    <Combobox
      items={items}
      autoComplete="none"
      value={selected}
      onValueChange={commit}
      itemToStringValue={(item: CellLineResponse) => item.id}
      itemToStringLabel={(item: CellLineResponse) => item.name ?? ''}
      disabled={disabled}
    >
      <ComboboxInput
        className={className}
        placeholder={isLoading ? 'Loading...' : placeholder}
        showClear
      />
      <ComboboxContent>
        <ComboboxEmpty>No cell lines found.</ComboboxEmpty>
        <ComboboxList>
          {(item: CellLineResponse) => (
            <ComboboxItem key={item.id} value={item}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
